import React from "react";
import { Box, Button, Typography, Container, useTheme, useMediaQuery } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImageIcon from '@mui/icons-material/Image';

/**
 * Full-screen wrong eye error component
 * Shows when wrong eye is detected (like image 6/7)
 * Always shows "Proceed with this image" option
 */
const WrongEyeError = ({ detectedEye, expectedEye, onReupload, onProceed }) => {
  const eyeDisplayName = detectedEye === 'left_eye' ? 'Left' : 'Right';
  const expectedEyeDisplayName = expectedEye === 'left' ? 'left' : 'right';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <div style={{ 
      width: "100%", 
      height: "100vh", 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center",
      position: "fixed",
      top: 0,
      left: 0,
      zIndex: 9999,
      backgroundColor: "rgba(249, 252, 255, 1)"
    }}>
      <Container sx={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center",
        textAlign: "center",
        maxWidth: { xs: "90%", sm: "500px", md: "480px" },
        padding: { xs: "16px", sm: "20px" },
        width: "100%"
      }}>
        {/* Warning Icon and Message */}
        <Box
          sx={{
            background: "#FFF9E6",
            border: "1px solid #FFE082",
            borderRadius: "8px",
            padding: { xs: "16px", sm: "20px" },
            marginBottom: { xs: "16px", sm: "20px" },
            display: "flex",
            alignItems: "center",
            gap: { xs: "10px", sm: "12px" },
            width: "100%"
          }}
        >
          <Box sx={{ 
            display: "flex",
            alignItems: "center",
            flexShrink: 0
          }}>
            <ImageIcon sx={{ color: "#F57C00", fontSize: { xs: "32px", sm: "40px" } }} />
          </Box>
          <Box sx={{ flex: 1, textAlign: { xs: "left", sm: "left" } }}>
            <Typography 
              fontSize={{ xs: "14px", sm: "16px" }}
              fontWeight={500} 
              sx={{ color: "#F57C00", mb: { xs: 0.5, sm: 1 }, wordBreak: "break-word" }}
            >
              The uploaded image is of {eyeDisplayName} eye
            </Typography>
            <Typography 
              fontSize={{ xs: "13px", sm: "14px" }}
              fontWeight={400} 
              sx={{ color: "#666", wordBreak: "break-word" }}
            >
              Upload an image of {expectedEyeDisplayName} eye.
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" }, 
          gap: { xs: "10px", sm: "12px" }, 
          width: "100%", 
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Button
            variant="outlined"
            onClick={onProceed}
            sx={{
              border: "1px solid #0D1E83",
              borderRadius: "8px",
              color: "#0D1E83",
              textTransform: "none",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 500,
              padding: { xs: "10px 20px", sm: "12px 32px" },
              width: { xs: "100%", sm: "auto" },
              flex: { xs: "none", sm: 1 },
              minWidth: { xs: "auto", sm: "200px" },
              '&:hover': {
                border: "1px solid #0D1E83",
                background: "rgba(13, 30, 131, 0.05)"
              }
            }}
          >
            Proceed with this image
          </Button>
          <Button
            variant="contained"
            onClick={onReupload}
            endIcon={<RefreshIcon />}
            sx={{
              background: 'linear-gradient(270deg, #051572 7.32%, #0820B4 92.68%)',
              borderRadius: "8px",
              color: "#FFF",
              textTransform: "none",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 500,
              padding: { xs: "8px 12px", sm: "8px 16px" },
              width: "fit-content",
              minWidth: "unset",
              maxWidth: "fit-content",
              flex: "none",
              '&:hover': {
                background: 'linear-gradient(270deg, #041461 7.32%, #071A9F 92.68%)',
              }
            }}
          >
            Re-upload
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default WrongEyeError;

